import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Download, Clock, XCircle, CheckCircle2 } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { getApiUrl } from '../utils/api';
import { DOWNLOADS_CHANGED, downloadQueuedFile } from '../lib/gatedDownload';

const API_URL = getApiUrl();

const minutesLeft = (iso) => Math.max(0, Math.ceil((new Date(iso) - Date.now()) / 60000));

const StatusLine = ({ r }) => {
  if (r.status === 'pending') return <span className="flex items-center gap-1 text-amber-600"><Clock className="w-3 h-3" /> Downloads automatically in {minutesLeft(r.available_at)} min</span>;
  if (r.status === 'rejected') return <span className="flex items-center gap-1 text-rose-600"><XCircle className="w-3 h-3" /> Download request rejected by Admin</span>;
  if (r.status === 'downloaded') return <span className="flex items-center gap-1 text-emerald-600"><CheckCircle2 className="w-3 h-3" /> Downloaded</span>;
  return <span className="text-indigo-600">Ready</span>;
};

export const DownloadRequestsTray = () => {
  const [items, setItems] = useState([]);
  const busy = useRef(new Set());

  const handle = useCallback(async (list) => {
    for (const r of list) {
      if (r.status === 'ready' && !busy.current.has(r.request_id)) {
        busy.current.add(r.request_id);
        try { await downloadQueuedFile(r); toast.success(`${r.label} downloaded`); } catch { busy.current.delete(r.request_id); }
      }
      if (r.status === 'rejected' && !r.rejection_seen) {
        toast.error(`Download request rejected by Admin — ${r.label}`, { duration: 10000 });
        axios.post(`${API_URL}/api/download-requests/mine/${r.request_id}/seen`, {}, { withCredentials: true }).catch(() => {});
      }
    }
  }, []);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/download-requests/mine`, { withCredentials: true });
      setItems(data.requests || []);
      handle(data.requests || []);
    } catch { /* silent */ }
  }, [handle]);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    window.addEventListener(DOWNLOADS_CHANGED, load);
    return () => { clearInterval(t); window.removeEventListener(DOWNLOADS_CHANGED, load); };
  }, [load]);

  if (!items.length) return null;
  const pending = items.filter((r) => r.status === 'pending').length;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="relative p-2 rounded-lg hover:bg-muted transition-colors" title="My downloads" data-testid="download-tray-btn">
          <Download className="w-5 h-5 text-slate-600" />
          {pending > 0 && <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 text-white text-[10px] font-semibold flex items-center justify-center" data-testid="download-tray-count">{pending}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="download-tray">
        <p className="px-4 py-3 border-b text-sm font-semibold text-slate-800">My downloads</p>
        <ul className="max-h-80 overflow-y-auto divide-y">
          {items.map((r) => (
            <li key={r.request_id} className="px-4 py-3 text-xs" data-testid={`download-item-${r.request_id}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium text-slate-800 truncate">{r.label}</span>
                {['ready', 'downloaded'].includes(r.status) && (
                  <button onClick={() => downloadQueuedFile(r).then(load).catch(() => toast.error('Download failed'))} className="text-indigo-600 hover:underline shrink-0" data-testid={`download-item-get-${r.request_id}`}>Download</button>
                )}
              </div>
              <p className="text-slate-400 mt-0.5">{new Date(r.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</p>
              <div className="mt-1" data-testid={`download-item-status-${r.request_id}`}><StatusLine r={r} /></div>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
};

export default DownloadRequestsTray;
